import { getTenant, formatCurrencyAmount } from '../../theme/tokens'

/**
 * Central locale-profile configuration.
 *
 * SINGLE SOURCE OF TRUTH for all market-/brand-specific values so that
 * re-skinning the prototype for a new client (different bank name, currency,
 * country, locale, tax regime, addresses) is a config-only change instead of
 * a screen-by-screen hunt for hardcoded literals.
 *
 * The active profile is derived from the current tenant (tenant.config.ts via
 * getTenant()). To onboard a new market, add a profile to PROFILES keyed by the
 * tenant locale (and update tenant.config.ts brand/currency/locale).
 */
export interface BusinessAddress {
  line1: string
  city: string
  postalCode: string
  country: string
}

export interface LocaleProfile {
  /** Display country for the active market. */
  country: string
  /** International dialing prefix, e.g. "+60". */
  callingCode: string
  /** Domain suffix used for company emails, e.g. "com.my". */
  emailTld: string
  /** Legal suffix for private-limited companies, e.g. "Sdn Bhd". */
  companySuffix: string
  /** Sample cities for mock data. */
  cities: string[]
  /** Sample street/address lines for mock data. */
  streets: string[]
  /** BCP-47 locale used for number formatting. */
  numberLocale: string
  /** BCP-47 locale used for date formatting. */
  dateLocale: string
  /** IANA timezone for date/time display. */
  timeZone: string
  /** Currencies offered in currency pickers (settlement currency first). */
  supportedCurrencies: string[]
  /** Countries offered in country pickers. */
  countries: string[]
  /** Consumption-tax label for this market, e.g. "SST", "GST", "VAT". */
  taxLabel: string
  /** Label for the tax registration field, e.g. "SST Number". */
  taxIdLabel: string
  /** Headquarters / registered business address for the brand. */
  businessAddress: BusinessAddress
  /** Short timezone abbreviation for display, e.g. "MYT". */
  timeZoneAbbr: string
  /** Settlement bank used for merchant payouts. */
  settlementBank: {
    /** Short code used in bank references / account labels, e.g. "BRKY". */
    shortCode: string
    /** Bank routing / SWIFT-style code. */
    routingNumber: string
  }
}

const MALAYSIA_PROFILE: LocaleProfile = {
  country: 'Malaysia',
  callingCode: '+60',
  emailTld: 'com.my',
  companySuffix: 'Sdn Bhd',
  cities: [
    'Kuala Lumpur',
    'George Town',
    'Johor Bahru',
    'Shah Alam',
    'Ipoh',
    'Petaling Jaya',
  ],
  streets: [
    'Jalan Ampang',
    'Jalan Tun Razak',
    'Jalan Sultan Ismail',
    'Persiaran KLCC',
    'Lebuh Pasar Besar',
    'Jalan Bukit Bintang',
  ],
  numberLocale: 'en-MY',
  dateLocale: 'en-GB',
  timeZone: 'Asia/Kuala_Lumpur',
  supportedCurrencies: ['MYR', 'USD', 'EUR', 'GBP', 'SGD'],
  countries: ['Malaysia', 'Singapore', 'Thailand', 'Indonesia', 'Brunei'],
  taxLabel: 'SST',
  taxIdLabel: 'SST Number',
  businessAddress: {
    line1: 'Menara Maybank, 100 Jalan Tun Perak',
    city: 'Kuala Lumpur',
    postalCode: '50050',
    country: 'Malaysia',
  },
  timeZoneAbbr: 'MYT',
  settlementBank: {
    shortCode: 'MBB',
    routingNumber: 'MBBEMYKL',
  },
}

// Keyed by tenant locale. Falls back to the active tenant's market profile.
const PROFILES: Record<string, LocaleProfile> = {
  'en-US': MALAYSIA_PROFILE,
  'en-MY': MALAYSIA_PROFILE,
  'ms-MY': MALAYSIA_PROFILE,
}

/** Returns the locale profile for the currently active tenant. */
export function getLocaleProfile(): LocaleProfile {
  const locale = getTenant().locale.default
  return PROFILES[locale] ?? MALAYSIA_PROFILE
}

// ─── Brand / currency (sourced from the tenant) ──────────────────────────

/** Active settlement currency code (e.g. "MYR"). */
export function currencyCode(): string {
  return getTenant().currency.code
}

/** Active brand / bank name (e.g. "CIMB"). */
export function bankName(): string {
  return getTenant().brand.name
}

/** Short timezone abbreviation for display (e.g. "MYT"). */
export function timeZoneAbbr(): string {
  return getLocaleProfile().timeZoneAbbr
}

export interface SettlementBank {
  /** Display name of the settlement bank (tenant brand, e.g. "CIMB"). */
  name: string
  /** Short code for references / account labels (e.g. "BRKY"). */
  shortCode: string
  /** Bank routing / SWIFT-style code. */
  routingNumber: string
}

/**
 * Settlement bank merchants are paid out to. Display name follows the active
 * tenant brand; short code and routing come from the market profile so a
 * re-skin updates payout account labels and bank references automatically.
 */
export function settlementBank(): SettlementBank {
  const { settlementBank: sb } = getLocaleProfile()
  return {
    name: bankName(),
    shortCode: sb.shortCode,
    routingNumber: sb.routingNumber,
  }
}

/** BCP-47 locale used for number formatting. */
export function numberLocale(): string {
  return getLocaleProfile().numberLocale
}

/** BCP-47 locale used for date formatting. */
export function dateLocale(): string {
  return getLocaleProfile().dateLocale
}

// ─── Central formatters ──────────────────────────────────────────────────

export interface MoneyOptions {
  /** Fraction digits to display. Defaults to 0. */
  fractionDigits?: number
  /** Override currency code. Defaults to the tenant currency. */
  currency?: string
}

/**
 * Format a major-unit amount as "<CODE> <grouped-number>", e.g. "MYR 1,234.56".
 * Matches the prototype's existing code-prefixed display style while sourcing
 * the currency code and grouping locale from the central config.
 */
export function formatMoney(
  amount: number,
  options: MoneyOptions = {},
): string {
  const { fractionDigits = 0, currency } = options
  const code = currency ?? currencyCode()
  const value = amount.toLocaleString(numberLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
  return `${code} ${value}`
}

/** Intl-style currency formatting via the theme layer (symbol-aware). */
export function formatCurrencyLocalized(
  amount: number,
  fractionDigits?: number,
): string {
  return formatCurrencyAmount(amount, {
    currency: currencyCode(),
    locale: numberLocale(),
    fractionDigits,
  })
}

/** Format a date using the central date locale + timezone. */
export function formatDate(
  date: Date | string | number,
  opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  },
): string {
  const d = date instanceof Date ? date : new Date(date)
  return d.toLocaleDateString(dateLocale(), {
    timeZone: getLocaleProfile().timeZone,
    ...opts,
  })
}

/** Format a date+time using the central date locale + timezone. */
export function formatDateTime(
  date: Date | string | number,
  opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
): string {
  const d = date instanceof Date ? date : new Date(date)
  return d.toLocaleString(dateLocale(), {
    timeZone: getLocaleProfile().timeZone,
    ...opts,
  })
}

/** Single-line formatted business address for the active brand. */
export function businessAddressString(): string {
  const { businessAddress: a } = getLocaleProfile()
  return `${a.line1}, ${a.postalCode} ${a.city}, ${a.country}`
}

// ─── Helpers built from the active profile ───────────────────────────────

/** "Acme Trading" => "Acme Trading Sdn Bhd" */
export function makeCompanyName(base: string): string {
  return `${base} ${getLocaleProfile().companySuffix}`
}

/** ("billing", "acme") => "billing@acme.com.my" */
export function makeEmail(localPart: string, domainBase: string): string {
  return `${localPart}@${domainBase}.${getLocaleProfile().emailTld}`
}

/** "33 4567" => "+60 33 4567" */
export function makePhone(subscriber: string): string {
  return `${getLocaleProfile().callingCode} ${subscriber}`
}

/** Deterministic city pick from the profile list. */
export function profileCity(index: number): string {
  const { cities } = getLocaleProfile()
  return cities[index % cities.length]
}

/** Deterministic street/address pick from the profile list. */
export function profileStreet(index: number): string {
  const { streets } = getLocaleProfile()
  return streets[index % streets.length]
}

/** Country name for the active profile. */
export function profileCountry(): string {
  return getLocaleProfile().country
}
